import React, { useEffect, useRef, useState } from 'react'
import cls from 'classnames'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import CloseIcon from './Icons/Close'
import Tooltip from './Tooltip'

const Chip = ({
  size = 'large',
  className,
  checked,
  label,
  onClose,
  onChange,
  checkedStyle = 'selected',
  disabled,
  prefixIcon,
  ignoreTooltip,
  id,
  onMouseDown,
  maxWidth,
  style,
  forwardedRef,
  bordered = false,
  tooltipContent,
  forceTooltip = false,
  tooltipProps = {},
}) => {
  const [isLabelOverflowed, setIsLabelOverflow] = useState(false)
  const labelRef = useRef()

  useEffect(() => {
    if (!labelRef.current) return

    setIsLabelOverflow(labelRef.current.scrollWidth > labelRef.current.clientWidth)
  }, [label])

  const resolvedTooltipProps = {
    align: { offset: [0, -10] },
    ...tooltipProps,
  }

  if (tooltipProps?.align) {
    resolvedTooltipProps.align = tooltipProps.align
  }

  const tooltipText = forceTooltip
    ? (tooltipContent ?? label)
    : isLabelOverflowed
      ? label
      : undefined

  return (
    <div
      className={className}
      onClick={onChange}
      ref={forwardedRef}
      onMouseDown={onMouseDown}
      style={style}
    >
      <div
        className={cls('chip-container', {
          pointer: !onClose,
          'small-size': size === 'small',
          'large-size': size === 'large',
          unselected: !checked && !disabled,
          selected: checked && !disabled && checkedStyle === 'selected',
          active: checked && !disabled && checkedStyle === 'active',
          disabled,
          bordered,
        })}
      >
        {prefixIcon && <div className="chip-icon">{prefixIcon}</div>}
        <Tooltip text={tooltipText} ignoreTooltip={ignoreTooltip} {...resolvedTooltipProps}>
          <div
            className="chip-label"
            ref={labelRef}
            style={maxWidth ? { maxWidth } : undefined}
            id={`chip-label-${id || ''}`}
          >
            {label}
          </div>
        </Tooltip>
        {!!onClose && (
          <div
            id={`chip-label-remove-${id || ''}`}
            className="chip-icon pointer"
            onClick={(e) => {
              e.stopPropagation()
              onClose()
            }}
          >
            <CloseIcon />
          </div>
        )}
      </div>
    </div>
  )
}

Chip.propTypes = {
  size: PropTypes.oneOf(['small', 'large']),
  className: PropTypes.string,
  checked: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  onClose: PropTypes.func,
  onChange: PropTypes.func,
  checkedStyle: PropTypes.oneOf(['selected', 'active']),
  disabled: PropTypes.bool,
  prefixIcon: PropTypes.element,
  onMouseDown: PropTypes.func,
  maxWidth: PropTypes.string,
  bordered: PropTypes.bool,
}

export default styled(Chip)`
  display: inline-flex;

  .chip-container {
    font-weight: 400;
    font-size: 12px;
    font-family: var(--t-typography-font-family-font-family-default) !important;
    box-sizing: content-box;
    border-radius: 100px;
    display: flex;
    gap: 3px;
    align-items: center;
    justify-content: center;
    cursor: default;

    :hover:not(.disabled):not(.selected) {
      color: var(--ds-button-hover);
      background-color: var(--ds-bg-pale-blue);
      text-shadow: 0 0 0.7px;

      svg > path {
        fill: var(--ds-button-hover);
      }
    }

    .chip-label {
      max-width: ${({ noMaxWidth }) => (noMaxWidth ? 'none' : '110px')};
      overflow: hidden;
      text-align: center;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .chip-icon {
      display: inline-flex;

      svg,
      img {
        width: 16px;
        height: 16px;
      }
    }
  }

  .small-size {
    height: 16px;
    padding: 2px 8px;
  }

  .large-size {
    height: 18px;
    padding: 4px 12px;
  }

  .selected {
    color: var(--primary-color);
    background-color: var(--ds-bg-pale-blue);

    svg > path {
      fill: var(--primary-color);
    }
  }

  .active {
    color: var(--primary);
    background-color: var(--ds-bg-pale-blue);
    text-shadow: 0 0 0.7px;

    svg > path {
      fill: var(--primary);
    }
  }

  .unselected {
    color: var(--sub-text-color);
    background-color: var(--ds-gray-8);

    svg > path {
      fill: var(--sub-text-color);
    }
  }

  .disabled {
    color: var(--ds-gray-5);
    background-color: var(--ds-gray-7);

    svg > path {
      fill: var(--ds-gray-5);
    }
  }

  .pointer:not(.selected) {
    cursor: pointer;
  }

  .bordered {
    border: 1px solid var(--ds-gray-6);
    border-radius: 4px;
    background: var(--ds-gray-9);
  }
`
